import type { DetailedSurvey } from "@survey-types/survey.core.types";
import type { Property } from "@survey-types/survey.property.types";

/**
 * Utility function to filter visible properties based on survey settings
 * Consolidates property filtering logic used across multiple components
 *
 * @param properties - Array of properties to filter
 * @param survey - Survey object containing visibility settings
 * @param propertyFilter - Optional specific property ID to filter for
 * @param usePropertyDetailsFlag - If true, uses showCurrentLocationPD; if false, uses showCurrentLocation
 * @returns Filtered array of visible properties
 */
export const getVisibleProperties = (
  properties: Property[] | undefined,
  survey: DetailedSurvey | null,
  propertyFilter?: string,
  usePropertyDetailsFlag: boolean = false,
): Property[] => {
  if (!properties) return [];

  return properties.filter((property) => {
    // Filter out hidden properties
    if (property.isHidden) return false;

    // If property is marked as current location, check the appropriate flag
    if (property.isCurrentLocation) {
      const showCurrentLocationFlag = usePropertyDetailsFlag
        ? survey?.showCurrentLocationPD
        : survey?.showCurrentLocation;

      if (!showCurrentLocationFlag) {
        return false;
      }
    }

    // If propertyFilter is provided, only include that specific property
    if (propertyFilter && property.propertyId !== propertyFilter) {
      return false;
    }

    return true;
  });
};

/**
 * Utility function to filter properties for map/legend display
 * Shows all non-hidden properties regardless of shortlisting status
 *
 * @param properties - Array of properties to filter
 * @returns Filtered array of visible properties for map display
 */
export const getMapVisibleProperties = (
  properties: Property[] | undefined,
): Property[] => {
  if (!properties) return [];

  return properties.filter((property) => {
    // Filter out hidden properties only
    if (property.isHidden) return false;

    return true;
  });
};

/**
 * Get property IDs from visible properties
 * Useful for API calls that need property ID arrays
 *
 * @param properties - Array of properties
 * @param survey - Survey object containing visibility settings
 * @param propertyFilter - Optional specific property ID to filter for
 * @param usePropertyDetailsFlag - If true, uses showCurrentLocationPD; if false, uses showCurrentLocation
 * @returns Array of property IDs, sorted for consistent cache keys
 */
export const getVisiblePropertyIds = (
  properties: Property[] | undefined,
  survey: DetailedSurvey | null,
  propertyFilter?: string,
  usePropertyDetailsFlag: boolean = false,
): string[] => {
  const visibleProperties = getVisibleProperties(
    properties,
    survey,
    propertyFilter,
    usePropertyDetailsFlag,
  );
  return visibleProperties.map((property) => property.propertyId).sort((a, b) => a.localeCompare(b)); // Sort for consistent cache keys
};
